import React, { useEffect, useRef } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { LockClosedIcon } from '@heroicons/react/24/outline'
import { useAuth } from '../../context/AuthContext'
import { BRAMKI_LOGOWANIA_WLACZONE, BRAMKI_ZAWSZE } from '../../config/bramki'
import { zdarzenie } from '../../lib/pomiar'

/**
 * Treści dostępne dopiero po zalogowaniu — wspólna mechanika dla całego serwisu.
 *
 * ═══ DWA TRYBY, BO MIEJSCA SĄ RÓŻNE ═══
 * `naklad`  — treść zostaje pod rozmyciem, karta z zachętą ląduje na wierzchu.
 *             Wymaga miejsca: pod kartą musi być widać, że coś tam jest.
 * `zamiast` — sama karta, bez tła. Do okien tak małych, że rozmyta treść
 *             i karta nie zmieszczą się obok siebie (np. okienko historii ceny).
 *
 * ═══ ROZMYTA WARSTWA JEST MARTWA ═══
 * `pointer-events-none` blokuje mysz, `inert` (ustawiany refem, bo React 18 nie
 * przepuszcza tego atrybutu z JSX) wyklucza poddrzewo z tabulacji i z czytników
 * ekranu. Bez `inert` dałoby się dojść Tabem do pól pod rozmyciem i ich użyć.
 *
 * ═══ CZEGO TO NIE ZAŁATWIA ═══
 * To zasłona w interfejsie, nie kontrola dostępu. Dane, które komponent zdążył
 * pobrać, nadal są w pamięci przeglądarki. Tam, gdzie treść ma NIE opuszczać
 * serwera, wywołanie API trzeba wstrzymać osobno (tak robi PriceHistoryChart)
 * albo zamknąć endpoint po stronie backendu.
 */

/**
 * Czy użytkownik ma widzieć treść spod bramki.
 *
 * Używaj TEGO zamiast pytać `useAuth()` wprost w miejscach, gdzie chodzi
 * o dostęp do treści — inaczej bramka wypada spod wspólnego wyłącznika
 * (config/bramki.js) i przy przywracaniu blokad ktoś ją przeoczy.
 *
 * `ustalanie` mówi, że jeszcze nie wiemy, kto jest zalogowany. Przy zdjętych
 * bramkach jest zawsze `false`: skoro treść i tak należy się każdemu, czekanie
 * na odpowiedź o sesji dokładałoby migotanie bez powodu.
 */
export const useDostepDoTresci = (bramka = null) => {
  const { isAuthenticated, loading } = useAuth()
  // Bramka z `BRAMKI_ZAWSZE` działa także przy zdjętych pozostałych (config/bramki.js).
  if (!BRAMKI_LOGOWANIA_WLACZONE && !BRAMKI_ZAWSZE[bramka]) return { odblokowane: true, ustalanie: false }
  return { odblokowane: isAuthenticated, ustalanie: loading }
}

/**
 * Czy przy odnośniku do narzędzia pokazać kłódkę „Po zalogowaniu” (menu, kafelek w asystencie).
 *
 * Bez klucza zawsze `false`: pusty klucz w `useDostepDoTresci` znaczy „wspólny wyłącznik”, a odnośnik bez
 * bramki nie powinien dostać kłódki tylko dlatego, że ktoś kiedyś włączy wszystkie bramki naraz. Dopóki
 * nie wiemy, kto jest zalogowany, kłódki nie ma, żeby nie mignęła zalogowanemu.
 */
export const useWymagaLogowania = (bramka) => {
  const { odblokowane, ustalanie } = useDostepDoTresci(bramka || null)
  return Boolean(bramka) && !odblokowane && !ustalanie
}

/**
 * Anonimowy pomiar (2026-10-15): niezalogowany zobaczył zachętę do logowania zamiast narzędzia z bramką
 * `bramka`. Jedno zdarzenie `bramka` na wejście na ekran, cel to klucz bramki. Bez klucza (wspólny wyłącznik)
 * nic nie wysyłamy. Pomiar nie wie, kto jest zalogowany, więc liczy wyłącznie samo wejście na zachętę.
 */
export const useZdarzenieBramki = (bramka, widoczna) => {
  useEffect(() => {
    if (bramka && widoczna) zdarzenie('bramka', bramka)
  }, [bramka, widoczna])
}

/** Mała kłódka obok nazwy narzędzia z podpisem dla myszy (`title`) i czytnika ekranu. */
export const KlodkaLogowania = ({ className = 'h-3.5 w-3.5' }) => (
  <span title="Po zalogowaniu" className="inline-flex flex-shrink-0 items-center text-gray-400">
    <LockClosedIcon className={className} aria-hidden="true" />
    <span className="sr-only"> (po zalogowaniu)</span>
  </span>
)

const ZACHETA_DOMYSLNA = {
  tytul: 'Dostęp tylko dla zalogowanych',
  opis: 'Rejestracja jest bezpłatna.',
}

/** Karta z zachętą. `kompaktowa` = wersja do ciasnych okien (mniejsze odstępy). */
export const KartaZachety = ({
  tytul = ZACHETA_DOMYSLNA.tytul,
  opis = ZACHETA_DOMYSLNA.opis,
  kompaktowa = false,
}) => {
  const location = useLocation()
  // `state.from` odsyła po zalogowaniu tam, skąd użytkownik przyszedł (obsługa
  // w Signin) — a nie na stronę główną, z której musiałby szukać drogi z powrotem.
  const skad = { from: location.pathname + location.search }

  return (
    <div className={`w-full rounded-2xl border border-gray-200 bg-white text-center ${
      kompaktowa ? 'p-4' : 'p-6 sm:p-7 max-w-md shadow-xl'}`}>
      <span className={`inline-flex items-center justify-center rounded-2xl bg-blue-50 text-blue-600 ${
        kompaktowa ? 'h-9 w-9 mb-2.5' : 'h-12 w-12 mb-4'}`}>
        <LockClosedIcon className={kompaktowa ? 'w-5 h-5' : 'w-6 h-6'} />
      </span>

      <h3 className={`font-bold text-gray-900 ${kompaktowa ? 'text-sm' : 'text-lg'}`}>{tytul}</h3>
      <p className={`mt-1.5 text-gray-600 leading-6 ${kompaktowa ? 'text-xs' : 'text-sm mt-2'}`}>{opis}</p>

      <Link
        to="/rejestracja" state={skad}
        className={`mt-4 flex w-full items-center justify-center rounded-lg bg-blue-600 text-white
                    font-semibold no-underline hover:bg-blue-500 active:bg-blue-700
                    transition-colors shadow-sm ${kompaktowa ? 'px-3 py-2 text-xs' : 'px-4 py-2.5 text-sm'}`}
      >
        Zarejestruj się za darmo w 60 sekund
      </Link>

      <p className={`mt-2 text-gray-500 ${kompaktowa ? 'text-[11px]' : 'text-sm mt-3'}`}>
        Masz już konto?{' '}
        <Link to="/logowanie" state={skad}
              className="font-semibold text-blue-600 hover:text-blue-700 underline underline-offset-2">
          Zaloguj się
        </Link>
      </p>
    </div>
  )
}

/**
 * Opakowanie treści: zalogowany dostaje `children`, reszta — zachętę.
 *
 * `maxWysokosc` przycina rozmyty podgląd (klasa Tailwinda, np. `max-h-80`).
 * Bez tego długa sekcja rozciąga stronę na kilka ekranów rozmytej treści,
 * przez którą nie da się przewinąć do niczego sensownego.
 */
export const BramkaTresci = ({
  children, tytul, opis, tryb = 'naklad', maxWysokosc = 'max-h-80', kompaktowa = true, bramka = null,
}) => {
  const { odblokowane, ustalanie } = useDostepDoTresci(bramka)
  useZdarzenieBramki(bramka, !ustalanie && !odblokowane)
  const tloRef = useRef(null)

  useEffect(() => {
    const el = tloRef.current
    if (el) el.inert = true
  })

  // Dopóki nie wiemy, kto jest zalogowany, NIE pokazujemy ani treści, ani zachęty:
  // jedno i drugie migałoby przy każdym wejściu niewłaściwej grupie użytkowników.
  if (ustalanie) {
    return <p className="text-sm text-gray-400 py-6 text-center">Wczytywanie…</p>
  }
  if (odblokowane) return children

  if (tryb === 'zamiast') {
    return (
      <div className="flex justify-center py-2">
        <KartaZachety tytul={tytul} opis={opis} kompaktowa={kompaktowa} />
      </div>
    )
  }

  return (
    <div className="relative">
      <div className={`relative overflow-hidden ${maxWysokosc}`} aria-hidden="true">
        <div ref={tloRef} className="pointer-events-none select-none blur-[3px] opacity-70">
          {children}
        </div>
        {/* Wygaszenie dołu, żeby podgląd nie urywał się ostrą krawędzią. */}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24
                        bg-gradient-to-b from-transparent to-white dark:to-gray-900" />
      </div>

      <div className="absolute inset-0 flex items-center justify-center px-4">
        <KartaZachety tytul={tytul} opis={opis} />
      </div>
    </div>
  )
}

export default BramkaTresci
